import { ChangeDetectorRef, Component, HostListener, Injector, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { OptionType } from 'ng/src/app/core/Option';
import { EntryCollection, Utils } from 'ng/src/app/core/utils';
import { ValueAlias } from 'ng/src/app/core/value';
import { BaseComponent } from 'ng/src/app/shared/base.component';
import { CommandPaletteAction, CommandPaletteService } from 'ng/src/app/shared/command-palette/command-palette.service';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'lb-command-container',
  templateUrl: './command-container.component.html',
  styleUrls: ['./command-container.component.scss']
})
export class CommandContainerComponent extends BaseComponent implements OnInit {
  @ViewChild("mainContainer", { read: ViewContainerRef, static: true }) container!: ViewContainerRef;
  
  readonly dialogVisible$ = new BehaviorSubject<boolean>(false);
  selected?: CommandPaletteAction;
  
  readonly searchValue$ = new BehaviorSubject<string>('');
  readonly searchValue = new ValueAlias(
    () => this.searchValue$.value,
    (val) => this.searchValue$.next(val)
  );
  
  actions$!: Observable<CommandPaletteAction[]>;
  groups$!: Observable<EntryCollection<CommandPaletteAction, -1 | NonNullable<CommandPaletteAction['group']>>>;
  flattenedGroups$!: Observable<CommandPaletteAction[]>;
  
  readonly dialogVisibleValue = new ValueAlias(
    () => this.dialogVisible$.value,
    (val) => this.dialogVisible$.next(val)
  );
  
  constructor(
    injector: Injector,
    public commandPaletteService: CommandPaletteService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super(injector);
  }
  
  ngOnInit(): void {
    fromEvent(document.body, 'keydown')
      .pipe(
        takeUntil(this.onDestroy$),
        filter(() => this.dialogVisibleValue.value)
      )
      .subscribe((e) => {
        const ev = e as KeyboardEvent;
        
        const actions$ = this.flattenedGroups$.pipe(
          takeUntil(this.onDestroy$),
          take(1)
        );
        
        switch (ev.key) {
          case 'Enter': {
            e.preventDefault();
            e.stopPropagation();
            if (!this.selected) {
              return;
            }
            this.execute(this.selected);
            break;
          }
          case 'ArrowUp':
            e.preventDefault();
            e.stopPropagation();
            actions$.subscribe((actions) => {
              if (!this.selected) {
                return;
              }
              
              const newSelected = actions.indexOf(this.selected) - 1;
              this.selected = actions[newSelected];
              this.scrollSelected();
            })
            break;
          case 'ArrowDown':
            e.preventDefault();
            e.stopPropagation();
            actions$.subscribe((actions) => {
              if (!this.selected) {
                this.selected = actions[0];
              }
              else {
                const newSelected = Utils.clamp(actions.indexOf(this.selected) + 1, 0, actions.length - 1);
                this.selected = actions[newSelected];
              }
              
              this.scrollSelected();
            })
            break;
        }
      });
    
    this.actions$ = this.commandPaletteService.actions.pipe(
      takeUntil(this.onDestroy$),
      switchMap(rActions => {
        const actions = [...rActions];
        
        return this.searchValue$.pipe(
          map((search) => {
            const toSearch = search.trim().toLowerCase();
            
            if (toSearch.length === 0) {
              return actions;
            }

            return actions.filter((a) => {
              return OptionType.arrayOfSome(...[
                () => this.getTokenScore(a.label) > 0,
                OptionType.nonNull(a.group)
                  .map((g) => () => this.getTokenScore(g) > 0),
                OptionType.nonNull(a.tags)
                  .map((t) => () => this.getTokenScore(...t) > 0)
              ])
                .some((predicate) => predicate())
            });
          })
        )
      }),
      shareReplay(1)
    )
    
    this.groups$ = this.actions$.pipe(
      map((filtered) => {
        const tokens = this.getSearchTokens();
        const actionScores = new Map<CommandPaletteAction, number>();
        const scoreAction = (action: CommandPaletteAction) => {
          if (actionScores.has(action)) {
            return actionScores.get(action)!;
          }
          
          let score = 0;
          if (tokens.length > 0) {
            OptionType.of(this.getTokenScore(action.label))
              .filter(n => n > 0)
              .whenSome((matches) => {
                score += (matches * 5);
              });
            
            OptionType.of(this.getTokenScore(...action.tags ?? []))
              .filter(n => n > 0)
              .whenSome((matches) => {
                score += matches;
              });
          }
          
          actionScores.set(action, score);
          return score;
        }
        
        const grouped = Utils.collectToEntries(filtered, (e) => {
          scoreAction(e);
          return e.group ?? -1;
        });
        
        const groupIndex = new Map<string | -1, number>();
        
        grouped.sort(Utils.createMultiSortFunction([
          {
            key: (group) => {
              let weight = 0;
              if (tokens.length == 0) {
                return weight;
              }
              
              const scores = group[1].map(a => scoreAction(a));
              weight = Math.max(...scores);
              
              return weight;
            },
            sort: 'desc'
          },
          {
            key: (group) => {
              if (groupIndex.has(group[0])) {
                return groupIndex.get(group[0])!;
              }
              
              let maxIndex = -1;
              for (const action of group[1]) {
                if (action.index != null && action.index < maxIndex) {
                  maxIndex = action.index;
                }
              }
              groupIndex.set(group[0], maxIndex);
              return maxIndex;
            },
            sort: 'desc'
          },
        ]));
        
        grouped.forEach(([, actions]) => {
          actions.sort(Utils.createMultiSortFunction([
            {
              key: (action) => {
                return scoreAction(action);
              },
              sort: 'desc',
            },
            {
              key: 'label'
            }
          ]));
        });
        
        return grouped;
      })
    );
    
    this.flattenedGroups$ = this.groups$.pipe(
      map(groups => {
        const flattened = [];
        for (const group of groups) {
          const actions = group[1];
          if (actions.length > 0) {
            flattened.push(...actions);
          }
        }
        
        return flattened;
      })
    )
    
  this.flattenedGroups$.subscribe((flattened) => {
      if (this.searchValue.value.length > 0) {
        this.selected = flattened[0];
      }
      else {
        this.selected = undefined;
      }
    });
    
    this.dialogVisible$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((visible) => {
        if (!visible) {
          this.selected = undefined;
          this.searchValue.value = '';
        }
      })
  }
  
  @HostListener('document:keydown', ['$event'])
  onKeyEvent(event: KeyboardEvent) {
    if (event.key !== 'k' || !(event.ctrlKey || event.metaKey)) {
      return;
    }
    
    this.dialogVisible$.next(true);
    event.stopPropagation();
    event.preventDefault();
  }
  
  execute(action: CommandPaletteAction) {
    this.dialogVisible$.next(false);
    action.command();
  }
  
  private getTokenScore(...values: string[]): number {
    let score = 0;
    const tokens = this.getSearchTokens();
    
    for (const token of tokens) {
      for (const value of values) {
        const lowerValue = value.toLowerCase();
        const index = lowerValue.indexOf(token);
        
        if (index > -1) {
          // Percentage of the token matches the value
          const matchRatio = token.length / lowerValue.length;
          const ratioBonus = matchRatio * 3;
          
          
          if (lowerValue === token) {
            score += 5 + ratioBonus;
          }
          else if (index === 0) {
            score += 3 + ratioBonus;
          }
          else {
            score += 1 + ratioBonus;
          }
        }
      }
    }
    
    return score;
  }
  
  private getSearchTokens() {
    return this.searchValue.value
      .split(' ')
      .map(t => t.trim().toLowerCase())
      .filter(t => t.length > 0);
  }
  
  private scrollSelected() {
    setTimeout(() => {
      const el: HTMLElement = this.container.element.nativeElement;
      if (!el) {
        return;
      }
      
      el.querySelector('.selected')?.scrollIntoView();
    });
  }
}

